<script setup lang="ts">
// Imports
import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'

// PrimeVue components
import PProgressBar from 'primevue/progressbar'
import PMessage from 'primevue/message'
import PSkeleton from 'primevue/skeleton'

// Stores
import { useReferentialStore } from '@/presentation/stores/referential/referential'
import { useLearningCoursesStore } from '@/presentation/stores/learning/learningCourses'

// Custom components and types
import CLearningCourseHeader from '@/presentation/components/learning/course/CLearningCourseHeader.vue'
import CLearningCourseModulesList from '@/presentation/components/learning/course/CLearningCourseModulesList.vue'
import CLearningCourseTasks from '@/presentation/components/learning/course/CLearningCourseTasks.vue'
import CBaseCard from '@/presentation/components/base/CBaseCard.vue'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus'
import { useLearningCourseAccessUtils } from '@/presentation/composables/learning/useLearningAccessUtils.ts'

// SETUP
const router = useRouter()
const toast = useToast()
const confirm = useConfirm()

// STORES
const referentialStore = useReferentialStore()
const learningCoursesStore = useLearningCoursesStore()

// STORE REFS
const { getAxisTitleFromDomainId } = referentialStore
const { currentCourse, loading, error } = storeToRefs(learningCoursesStore)
const { updateLearningCourseStatus } = learningCoursesStore
const { canPublishCourse } = useLearningCourseAccessUtils()

// LOCAL STATE

// PROPS
const props = defineProps<{
  courseId: number
}>()

// COMPUTED
const isLoading = computed(() => loading.value.fetchCourse || loading.value.updateCourseStatus)
const hasError = computed(() => error.value && error.value.hasError)
const errorMessage = computed(() => (error.value?.hasError ? error.value.message : ''))

// LIFECYCLE HOOKS

// METHODS
async function publishCourse() {
  if (!currentCourse.value) return

  confirm.require({
    message: `Êtes-vous sûr de vouloir publier le parcours "${currentCourse.value.title}" ?`,
    header: 'Confirmation de publication',
    icon: 'pi pi-check-circle',
    acceptLabel: 'Oui, publier',
    rejectLabel: 'Annuler',
    accept: async () => {
      try {
        await updateLearningCourseStatus(props.courseId, LearningStatus.Values.ACTIVE)
        toast.add({
          severity: 'success',
          summary: 'Parcours publié',
          detail: 'Le parcours a été publié avec succès',
          life: 3000
        })
      } catch (error) {
        console.error('Erreur lors de la publication du parcours:', error)
        toast.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Une erreur est survenue lors de la publication du parcours',
          life: 5000
        })
      }
    }
  })
}

function goBackToList() {
  router.push({ name: 'learning-courses-list' })
}

onMounted(async () => {
  await learningCoursesStore.fetchCourse(props.courseId, {
    withModules: true,
    status: [LearningStatus.Values.ACTIVE, LearningStatus.Values.DRAFT]
  })
})
</script>

<template>
  <c-learning-course-header
    :status="currentCourse?.status"
    :title="currentCourse?.title ? `Parcours : ${currentCourse.title}` : 'Modification du parcours'"
    :back-to="{
      label: 'Retour à la liste des parcours',
      route: { name: 'learning-courses-list' }
    }"
    :primary-button="{
      label: 'Publier le parcours',
      onClick: publishCourse,
      disabled: isLoading
        || !currentCourse
        || currentCourse.status === LearningStatus.Values.ACTIVE
        || !canPublishCourse(currentCourse)
    }"
  />

  <div class="flex max-h-full flex-col overflow-y-auto pb-8">
    <!-- Message d'erreur global -->
    <p-message v-if="hasError" severity="error" class="mx-8 mt-4 mb-4">
      {{ errorMessage }}
    </p-message>

    <!-- Indicateur de chargement -->
    <p-progress-bar v-if="isLoading" mode="indeterminate" class="mx-8 mt-4 mb-4" />

    <!-- Skeleton loader pendant le chargement -->
    <div v-if="isLoading" class="flex w-full flex-col items-start gap-8 px-8">
      <p-skeleton width="100%" height="5rem" />
      <p-skeleton width="100%" height="10rem" />
      <p-skeleton width="100%" height="15rem" />
    </div>

    <!-- Contenu principal -->
    <div v-else-if="currentCourse" class="flex w-full flex-col items-start gap-8 px-8">
      <c-base-card size="auto">
        <template #content>
          <div>
            <div class="font-bold">Axe</div>
            <div class="mt-1">
              {{ getAxisTitleFromDomainId(currentCourse.domain.id) }}
            </div>
          </div>

          <div>
            <div class="-color font-bold">Domaine</div>
            <div class="mt-1">
              {{ currentCourse.domain.title }}
            </div>
          </div>
        </template>
      </c-base-card>

      <c-learning-course-tasks
        v-if="currentCourse.domain?.tasks?.length"
        :tasks="currentCourse.domain.tasks"
        :learning-course-id="currentCourse.id"
      />
      <p-message
        v-else-if="!currentCourse.domain?.tasks?.length"
        severity="info"
        class="mb-4 w-full"
      >
        Aucune tâche n'est associée à ce domaine
      </p-message>

      <c-learning-course-modules-list :learning-course-id="currentCourse.id" />
    </div>

    <!-- Message si le cours n'est pas trouvé -->
    <div v-else-if="!isLoading" class="flex flex-col items-center justify-center p-8 text-center">
      <i class="pi pi-exclamation-triangle mb-4 text-4xl text-yellow-500"></i>
      <h3 class="mt-0 mb-2">Parcours introuvable</h3>
      <p class="text-surface-700 dark:text-surface-100">
        Le parcours demandé n'existe pas ou n'est plus disponible.
      </p>
      <button class="bg-primary mt-4 rounded p-2 text-white" @click="goBackToList">
        Retour à la liste
      </button>
    </div>
  </div>
</template>
